/**
 * API Client service
 * Centralized axios instance with configuration for API requests
 */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import config from '@config';
import authService from './authServiceFactory';
import { showToast } from '@utils/toast';

// Create axios instance with base configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: config.apiTimeout,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
});

// Request interceptor - add MSAL token to requests
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Get access token from active auth provider
      const service = await authService;
      const token = await service.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('No token available for request');
      }
    } catch (error: unknown) {
      console.error('Error getting access token:', error);
      // Don't block the request if token acquisition fails
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Formats a duration in seconds as "1h 5m 30s" (omitting zero-valued larger
// units), for the RateLimit-Reset countdown shown to the user on 429s.
const formatWaitTime = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (hours > 0 || minutes > 0) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);

  return parts.join(' ');
};

// Response interceptor - handle common errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle unauthorized errors (401)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh token
        const service = await authService;
        const token = await service.getAccessToken();
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Redirect to login if refresh fails
        console.error('Token refresh failed:', refreshError);
        window.location.href = '/login';
        throw refreshError;
      }
    } else if (error.response?.status === 429) {
      // Rate limited. No auto-retry here (risks a retry storm) - just surface
      // how long the client should wait, per RateLimit-Reset (seconds until reset).
      const resetSeconds = Number(error.response.headers?.['ratelimit-reset']);
      const message =
        Number.isFinite(resetSeconds) && resetSeconds > 0
          ? `Too many requests. Please try again in ${formatWaitTime(resetSeconds)}.`
          : 'Too many requests. Please try again later.';
      showToast.error(message);
    }

    throw error;
  }
);

// Helper method for making API requests
const makeRequest = async <T>(config: AxiosRequestConfig): Promise<T> => {
  try {
    const response = await apiClient.request<T>(config);
    return response.data;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// Export API request methods
const apiService = {
  get: <T>(url: string, params = {}) => makeRequest<T>({ method: 'get', url, params }),
  // Binary GET: returns the raw Blob (e.g. CSV export) so axios streams it to a
  // Blob instead of buffering and JSON-transforming a large string.
  getBlob: (url: string, params = {}) => makeRequest<Blob>({ method: 'get', url, params, responseType: 'blob' }),
  post: <T>(url: string, data = {}) => makeRequest<T>({ method: 'post', url, data }),
  put: <T>(url: string, data = {}) => makeRequest<T>({ method: 'put', url, data }),
  patch: <T>(url: string, data = {}) => makeRequest<T>({ method: 'patch', url, data }),
  delete: <T>(url: string, data?: unknown) => makeRequest<T>({ method: 'delete', url, data })
};

export default apiService;
